import { useState } from "react";
import { useTranslation } from "react-i18next";

interface FeedbackDialogProps {
    isOpen: boolean;
    onClose: () => void;
    sessionId: string;
    onSubmit?: (feedback: { score?: number; text?: string }) => Promise<void>;
}

/**
 * Simple feedback dialog without external UI library dependencies
 * Collects user ratings and comments after a session
 */
export function FeedbackDialog({ isOpen, onClose, sessionId, onSubmit }: FeedbackDialogProps) {
    const { t } = useTranslation();
    const [rating, setRating] = useState<number>(0);
    const [feedbackText, setFeedbackText] = useState("");
    const [hoveredStar, setHoveredStar] = useState<number>(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async () => {
        if (rating === 0) {
            alert("Please select a rating before submitting");
            return;
        }

        setIsSubmitting(true);

        try {
            // Use provided onSubmit handler if available, otherwise default fetch
            if (onSubmit) {
                await onSubmit({ score: rating, text: feedbackText.trim() || undefined });
            } else {
                const response = await fetch("/api/submit-feedback", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        session_id: sessionId,
                        rating,
                        feedback_text: feedbackText.trim() || undefined,
                    }),
                });

                if (!response.ok) {
                    throw new Error("Failed to submit feedback");
                }
            }

            setSubmitted(true);

            setTimeout(() => {
                onClose();
                setTimeout(() => {
                    setRating(0);
                    setFeedbackText("");
                    setSubmitted(false);
                }, 300);
            }, 2000);
        } catch (error) {
            console.error("Error submitting feedback:", error);
            alert("Failed to submit feedback. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
        }}>
            <div className="bg-slate-900 dark:bg-slate-800 border border-slate-700" style={{
                padding: "24px",
                borderRadius: "8px",
                maxWidth: "500px",
                width: "90%"
            }}>
                {!submitted ? (
                    <>
                        <h2 className="text-slate-100 dark:text-white" style={{ margin: "0 0 8px 0", fontSize: "20px", fontWeight: "600" }}>
                            {t('feedback.title')}
                        </h2>
                        <p className="text-slate-400 dark:text-slate-300" style={{ margin: "0 0 24px 0", fontSize: "14px" }}>
                            {t('feedback.subtitle')}
                        </p>

                        <div style={{ textAlign: "center", marginBottom: "24px" }}>
                            <p className="text-slate-200 dark:text-slate-100" style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "500" }}>
                                {t('feedback.rateExperience')}
                            </p>
                            <div style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        key={star}
                                        type="button"
                                        onClick={() => setRating(star)}
                                        onMouseEnter={() => setHoveredStar(star)}
                                        onMouseLeave={() => setHoveredStar(0)}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: "4px",
                                            fontSize: "32px"
                                        }}
                                    >
                                        <span style={{
                                            color: star <= (hoveredStar || rating) ? "#FFC107" : "#475569"
                                        }}>
                                            ★
                                        </span>
                                    </button>
                                ))}
                            </div>
                            {rating > 0 && (
                                <p className="text-slate-400 dark:text-slate-300" style={{ marginTop: "8px", fontSize: "14px" }}>
                                    {rating === 1 && t('feedback.ratingPoor')}
                                    {rating === 2 && t('feedback.ratingFair')}
                                    {rating === 3 && t('feedback.ratingGood')}
                                    {rating === 4 && t('feedback.ratingVeryGood')}
                                    {rating === 5 && t('feedback.ratingExcellent')}
                                </p>
                            )}
                        </div>

                        <div style={{ marginBottom: "24px" }}>
                            <label htmlFor="feedback" className="text-slate-200 dark:text-slate-100" style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "500" }}>
                                {t('feedback.commentsLabel')}
                            </label>
                            <textarea
                                id="feedback"
                                className="bg-slate-800 dark:bg-slate-700 text-slate-100 dark:text-white border-slate-600 dark:border-slate-500 placeholder-slate-400"
                                placeholder={t('feedback.commentsPlaceholder')}
                                value={feedbackText}
                                onChange={(e) => setFeedbackText(e.target.value)}
                                rows={4}
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    fontFamily: "inherit",
                                    resize: "none"
                                }}
                            />
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={isSubmitting}
                                className="bg-slate-700 hover:bg-slate-600 text-slate-100 border-slate-600"
                                style={{
                                    padding: "8px 16px",
                                    borderRadius: "4px",
                                    cursor: isSubmitting ? "not-allowed" : "pointer",
                                    fontSize: "14px"
                                }}
                            >
                                {t('feedback.skip')}
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={isSubmitting || rating === 0}
                                className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-slate-600 disabled:cursor-not-allowed"
                                style={{
                                    padding: "8px 16px",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    opacity: (isSubmitting || rating === 0) ? 0.5 : 1
                                }}
                            >
                                {isSubmitting ? t('feedback.submitting') : t('feedback.submit')}
                            </button>
                        </div>
                    </>
                ) : (
                    <div style={{ textAlign: "center", padding: "32px 0" }}>
                        <h2 className="text-slate-100 dark:text-white" style={{ margin: "0 0 8px 0", fontSize: "20px", fontWeight: "600" }}>
                            {t('feedback.thankYouTitle')}
                        </h2>
                        <p className="text-slate-400 dark:text-slate-300" style={{ margin: 0, fontSize: "14px" }}>
                            {t('feedback.thankYouMessage')}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
